import {
  sessionEngagementKindSchema,
  sessionLocationKindSchema,
} from "@/modules/classes/domain/contracts/sessions";
import { z } from "zod";
import { offSiteDistanceSchema } from "../lib/off-site-distance";

const bulkSessionLocationFields = {
  locationKind: sessionLocationKindSchema
    .nullish()
    .transform((v) => v ?? "studio"),
  offSiteLabel: z.union([z.string(), z.null()]).optional(),
  offSiteDistance: offSiteDistanceSchema.nullish(),
  engagementKind: sessionEngagementKindSchema
    .nullish()
    .transform((v) => v ?? "standard"),
  roomId: z.string().optional(),
};

function refineBulkSessionOffSiteDistance(
  data: {
    locationKind: "studio" | "off_site";
    offSiteDistance?: "near" | "far" | null;
    roomId?: string;
  },
  ctx: z.RefinementCtx,
) {
  if (data.locationKind === "studio" && data.offSiteDistance != null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "ສະຕູດິໂອບໍ່ໃຊ້ໄລຍະນອກສະຖານທີ່",
      path: ["offSiteDistance"],
    });
  }
}

/**
 * Schema สำหรับ Bulk Schedule Private Lessons
 */
export const BulkScheduleRequestSchema = z.object({
  packageId: z.string().min(1, "ຕ້ອງເລືອກ package"),
  instructorIds: z.array(z.string()).min(1, "ຕ້ອງເລືອກຢ່າງນ້ອຍ 1 instructor"),
  sessions: z
    .array(
      z
        .object({
          date: z.string().min(1, "ຕ້ອງເລືອກວັນທີ"),
          startTime: z.string().min(1, "ຕ້ອງເລືອກເວລາເລີ່ມຕົ້ນ"),
          endTime: z.string().min(1, "ຕ້ອງເລືອກເວລາສິ້ນສຸດ"),
          durationMinutes: z.number().min(1, "ຕ້ອງມີລາຍການຢ່າງໜ້ອຍ 1 ລາຍການ"),
          /** ຖ້າບໍ່ສົ່ງ ຫຼື ວ່າງ → ໃຊ້ instructorIds ລະດັບ request */
          instructorIds: z.array(z.string()).optional(),
          notes: z.string().optional(),
          ...bulkSessionLocationFields,
        })
        .refine(
          (data) =>
            data.locationKind !== "studio" ||
            (data.roomId != null && data.roomId.trim().length > 0),
          {
            message: "ຕ້ອງເລືອກຫ້ອງ",
            path: ["roomId"],
          },
        )
        .refine(
          (data) =>
            data.locationKind !== "off_site" ||
            data.roomId == null ||
            data.roomId.trim().length === 0,
          {
            message: "ນອກສະຖານທີ່ບໍ່ໃຊ້ຫ້ອງສະຕູດິໂອ",
            path: ["roomId"],
          },
        )
        .superRefine(refineBulkSessionOffSiteDistance),
    )
    .min(1, "ຕ້ອງມີລາຍການຢ່າງໜ້ອຍ 1 ລາຍການ"),
});

export type BulkScheduleRequestDTO = z.infer<typeof BulkScheduleRequestSchema>;

/** ຕາຕະລາງເລີ່ມຕົ້ນສົ່ງຕອນຂາຍແພັກເກດ (ບໍ່ມີ packageId — ຝັ່ງ backend ຕໍ່ດ້ວຍ id ຫຼັງ insert) */
export const PrivateLessonInitialScheduleOnSellSchema =
  BulkScheduleRequestSchema.omit({ packageId: true });

export type PrivateLessonInitialScheduleOnSellDTO = z.infer<
  typeof PrivateLessonInitialScheduleOnSellSchema
>;

/**
 * Response สำหรับ Bulk Schedule
 */
export const BulkScheduleResponseSchema = z.object({
  success: z.boolean(),
  createdCount: z.number(),
  bookings: z.array(
    z.object({
      id: z.string(),
      packageId: z.string(),
      date: z.string(),
      startTime: z.string(),
      endTime: z.string(),
      durationMinutes: z.number(),
      locationKind: sessionLocationKindSchema,
      offSiteLabel: z.string().nullable(),
      offSiteDistance: offSiteDistanceSchema.nullable(),
      engagementKind: sessionEngagementKindSchema,
      roomId: z.string().nullable(),
      instructorIds: z.array(z.string()),
      status: z.enum(["scheduled", "completed", "cancelled"]),
    }),
  ),
  updatedPackage: z.object({
    id: z.string(),
    remainingHours: z.number(),
  }),
});

export type BulkScheduleResponseDTO = z.infer<
  typeof BulkScheduleResponseSchema
>;

/**
 * Schema สำหรับ Check Availability
 */
export const CheckAvailabilityRequestSchema = z.object({
  sessions: z.array(
    z
      .object({
        date: z.string(),
        startTime: z.string(),
        endTime: z.string(),
        instructorIds: z.array(z.string()),
        ...bulkSessionLocationFields,
      })
      .refine(
        (data) =>
          data.locationKind !== "studio" ||
          (data.roomId != null && data.roomId.trim().length > 0),
        {
          message: "ຕ້ອງເລືອກຫ້ອງ",
          path: ["roomId"],
        },
      )
      .refine(
        (data) =>
          data.locationKind !== "off_site" ||
          data.roomId == null ||
          data.roomId.trim().length === 0,
        {
          message: "ນອກສະຖານທີ່ບໍ່ໃຊ້ຫ້ອງສະຕູດິໂອ",
          path: ["roomId"],
        },
      )
      .superRefine(refineBulkSessionOffSiteDistance),
  ),
});

export type CheckAvailabilityRequestDTO = z.infer<
  typeof CheckAvailabilityRequestSchema
>;

/**
 * Response สำหรับ Check Availability
 */
export const CheckAvailabilityResponseSchema = z.object({
  available: z.boolean(),
  conflicts: z.array(
    z.object({
      sessionIndex: z.number(),
      type: z.enum(["room", "instructor"]),
      message: z.string(),
      conflictingBooking: z
        .object({
          id: z.string(),
          date: z.string(),
          startTime: z.string(),
          endTime: z.string(),
        })
        .optional(),
    }),
  ),
});

export type CheckAvailabilityResponseDTO = z.infer<
  typeof CheckAvailabilityResponseSchema
>;
